import {
  faLink,
  faPlus,
  faTableColumns,
  faWindowRestore,
  faXmark,
} from "@fortawesome/pro-regular-svg-icons";

import { CopyTextIcon } from "src/base-components/CopyTextIcon";
import { IconButton } from "src/design-system/Icon";
import { Tooltip } from "src/design-system/Tooltip";
import { usePowertools } from "src/powertools/hooks";
import { useWorkspaceContext } from "src/router/routerContextHooks";
import { getBaseUrl, getUrlToSessionDebugPage } from "src/router/urls";
import { useUnifiedCopilotChatSession } from "src/unifiedCopilot/chat/useUnifiedCopilotChatSession";
import {
  type UnifiedCopilotCompanionMode,
  useUnifiedCopilotDisplay,
} from "src/unifiedCopilot/UnifiedCopilotDisplayProvider";
import { UnifiedCopilotSessionHistoryDropdown } from "src/unifiedCopilot/UnifiedCopilotSessionHistoryDropdown";

type HeaderActionTooltipPlacement = "top" | "bottom";

/** Why the new-conversation action is unavailable when it is. */
const NEW_CONVERSATION_UNAVAILABLE_TOOLTIP =
  "You are already in a new conversation";

export const UnifiedCopilotNewConversationButton: React.FC<{
  tooltipPlacement?: HeaderActionTooltipPlacement;
}> = ({ tooltipPlacement = "bottom" }) => {
  const { isUnusedSession, resetSession } = useUnifiedCopilotChatSession();

  return (
    // The disabled button still hovers, so the tooltip is where the reason the
    // action is unavailable gets told.
    <Tooltip
      delay={400}
      placement={tooltipPlacement}
      title={
        isUnusedSession
          ? NEW_CONVERSATION_UNAVAILABLE_TOOLTIP
          : "Start a new conversation"
      }
      asChild
    >
      <IconButton
        aria-label="Start a new conversation"
        dataLoc="unified-copilot-new-conversation"
        disabled={isUnusedSession}
        icon={faPlus}
        size="xs"
        onClick={resetSession}
      />
    </Tooltip>
  );
};

export const UnifiedCopilotSessionDebugActions: React.FC<{
  tooltipPlacement?: HeaderActionTooltipPlacement;
}> = ({ tooltipPlacement = "bottom" }) => {
  const { messages, sessionState, sessionId } = useUnifiedCopilotChatSession();
  const { orgId, workspace } = useWorkspaceContext();
  const hasPowertools = usePowertools();

  // The session id is minted client-side and pre-warmed before the first
  // message, so it only names a session support can look up once the
  // conversation has been started.
  const isNewConversation = sessionState === "idle" && messages.length === 0;
  if (!hasPowertools || isNewConversation || sessionId === null) return null;

  return (
    <>
      <CopyTextIcon
        dataLoc="unified-copilot-copy-session-id"
        size="xs"
        tooltip="Copy session ID"
        tooltipPlacement={tooltipPlacement}
        value={sessionId}
      />
      <CopyTextIcon
        dataLoc="unified-copilot-copy-debug-url"
        icon={faLink}
        size="xs"
        tooltip="Copy debug view URL"
        tooltipPlacement={tooltipPlacement}
        value={
          getBaseUrl() +
          getUrlToSessionDebugPage(orgId, workspace.id, {
            kind: "copilot",
            sessionId,
          })
        }
      />
    </>
  );
};

export const UnifiedCopilotSwitchModeButton: React.FC<{
  mode: UnifiedCopilotCompanionMode;
  tooltipPlacement?: HeaderActionTooltipPlacement;
}> = ({ mode, tooltipPlacement = "bottom" }) => {
  const { switchMode } = useUnifiedCopilotDisplay();
  const isSidebarDestination = mode === "sidebar";
  const label = isSidebarDestination
    ? "Move Taktile Agent to sidebar"
    : "Open Taktile Agent as floating window";

  return (
    <Tooltip delay={400} placement={tooltipPlacement} title={label} asChild>
      <IconButton
        aria-label={label}
        dataLoc={`unified-copilot-switch-to-${mode}`}
        icon={isSidebarDestination ? faTableColumns : faWindowRestore}
        size="xs"
        onClick={() => switchMode(mode)}
      />
    </Tooltip>
  );
};

export const UnifiedCopilotHeader: React.FC<{
  surface: "page" | "sidebar";
}> = ({ surface }) => {
  const { hide } = useUnifiedCopilotDisplay();

  return (
    <header
      className="flex h-13 shrink-0 items-center justify-between border-gray-200 border-b px-3"
      data-loc={`unified-copilot-${surface}-header`}
    >
      <UnifiedCopilotSessionHistoryDropdown />
      <div className="flex items-center gap-1">
        <UnifiedCopilotSessionDebugActions />
        <UnifiedCopilotNewConversationButton />
        {surface === "sidebar" && (
          <>
            <UnifiedCopilotSwitchModeButton mode="floating" />
            <Tooltip
              delay={400}
              placement="bottom"
              title="Hide Taktile Agent"
              asChild
            >
              <IconButton
                aria-label="Hide Taktile Agent"
                dataLoc="unified-copilot-hide-sidebar"
                icon={faXmark}
                size="xs"
                onClick={hide}
              />
            </Tooltip>
          </>
        )}
      </div>
    </header>
  );
};
